import axios, { AxiosError, Method } from 'axios';
import * as React from 'react';
import { FormEvent, useRef, useState } from 'react';

import DefaultErrorResponse from '../../types/ErrorResponse';
import { useSession } from '../SessionContext/SessionContext';

import FormContext from './FormContext';

type JsonFormProps<SuccessResponse, ErrorResponse> = {
    onSubmit?: (data: FormData) => void;
    onSuccess?: (data: SuccessResponse) => void;
    onError?: (data: ErrorResponse | undefined, statusCode: number) => void;
    onBeforeSubmit?: (data: FormData) => PromiseLike<any> | void | false;
    modifyFormData?: (data: FormData) => FormData;
    resetOnSuccess?: boolean;
} & Omit<React.HTMLProps<HTMLFormElement>, 'onError' | 'onSubmit'>;

const JsonForm = <
    SuccessResponse extends Record<string, unknown>,
    ErrorResponse extends Record<string, unknown> = DefaultErrorResponse,
>({
    onSubmit,
    onSuccess,
    onError,
    modifyFormData,
    onBeforeSubmit,
    resetOnSuccess = false,
    disabled = false,
    children,
    ...formProps
}: JsonFormProps<SuccessResponse, ErrorResponse>) => {
    const [submitting, setSubmitting] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const session = useSession();

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        submit();
    };

    const handleSubmitCompleted = () => {
        if (!formRef.current) {
            return;
        }

        setSubmitting(false);
    };

    const submit = async () => {
        if (submitting || !formRef.current) {
            return;
        }

        const form = formRef.current;
        let formData = new FormData(form);
        formData = modifyFormData ? modifyFormData(formData) : formData;

        if (session.tenantId) {
            formData.append('tenantId', session.tenantId);
        }

        if (onBeforeSubmit) {
            const proceedSubmitting = await onBeforeSubmit(formData);
            if (proceedSubmitting === false) {
                return;
            }
        }

        if (onSubmit) {
            onSubmit(formData);
        }

        setSubmitting(true);

        axios
            .request<SuccessResponse>({
                url: form.action,
                method: form.method as Method,
                data: formData,
            })
            .then((response) => {
                onSuccess && onSuccess(response.data);

                if (!formRef.current) {
                    return;
                }

                setHasErrors(false);

                if (resetOnSuccess) {
                    formRef.current.reset();
                }
            })
            .catch((error: AxiosError<ErrorResponse>) => {
                if (!error.response) {
                    console.error(error);
                    return;
                }

                const { status, data } = error.response;
                onError && onError(data, status);

                if (!formRef.current) {
                    return;
                }

                setHasErrors(true);
            })
            .then(handleSubmitCompleted);
    };

    return (
        <form
            {...formProps}
            onSubmit={handleSubmit}
            ref={formRef}
            aria-disabled={disabled}
        >
            <FormContext.Provider
                value={{ submitting, hasErrors, submit, disabled }}
            >
                {children}
            </FormContext.Provider>
        </form>
    );
};

export default JsonForm;
